import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Container, NewEraMark, buttonClasses, cn, useScrollSpy } from '@nexa/ui';
import { useContactDialog } from '@/core/contact';
import { APP_URL } from '@/core/config/site.ts';
import { useCopy } from '@/core/i18n';
import { LanguageSwitcher } from './LanguageSwitcher.tsx';
import { PageLink } from './PageLink.tsx';
import { useHomeAnchor } from './homeAnchor.ts';

/**
 * Header sticky — `template.html:580`. Cao 68px từ `md`, 56px ở mobile.
 *
 * Nav là 6 ANCHOR TRONG TRANG, không phải liên kết sang trang khác, nên chúng KHÔNG đi qua
 * `PageLink`: contract §1.1 nói về liên kết giữa các trang. Logo thì ngược lại — thiết kế cho nó
 * trỏ `#top`, nhưng header còn render trên trang 404 (nơi không có `#top`), và ở đó một logo
 * không đi đâu là ngõ cụt. Dùng `PageLink` giữ được cả hai: trên trang chủ nó về đúng trang chủ,
 * trên 404 nó là lối ra.
 *
 * NỢ CÓ CHỦ Ý (plan lô A′, D-A′-1): 6 anchor trỏ vào section chưa dựng cho tới lô D. Điều kiện
 * đóng nợ: lô D dựng xong `#faq`. Đừng "sửa" bằng cách bỏ bớt mục nav.
 *
 * Nav xuất hiện từ `xl` (1280), KHÔNG phải `lg` — `reference.md` §2 dòng `header` khai `md`
 * (768–1279) là "ẩn nav, hiện hamburger". Bản đầu dựng ở `lg` và ở 1024px thì logo + 6 nav +
 * lang + sign-in + CTA không đủ chỗ: mọi nhãn vỡ thành hai dòng ("Tính / năng", "Đăng / nhập").
 * Không phép đo tự động nào bắt được — nó WRAP chứ không tràn và cũng không cắt chữ, nên cổng
 * báo sạch. Ảnh 1024 của ma trận §11.5 mới là thứ nói ra.
 */

/**
 * TÁM section được theo dõi, xếp theo THỨ TỰ TÀI LIỆU — không phải 6 mục nav.
 *
 * Hai điều bắt buộc, cả hai đều là hành vi của `useScrollSpy`:
 *
 * 1. `pm` và `knowledge` phải CÓ MẶT ở đây dù nav không có mục cho chúng. Hook chỉ observe id
 *    được truyền vào và chỉ tra bảng gộp cho id nó thấy; thiếu `pm` thì cuộn qua `#pm` làm nav
 *    tắt sạch — trông hệt như hỏng. (Bản đầu của plan lô A′ truyền đúng 6 mục nav và dính lỗi
 *    này; test `section không có mục nav riêng…` bắt được.)
 * 2. Thứ tự phải là thứ tự trong trang: khi hai section cùng nằm trong dải đọc, hook lấy phần
 *    tử ĐẦU theo thứ tự khai — tức section trên cùng. Sắp lại theo bảng chữ cái là làm mục nav
 *    nhảy ngược khi cuộn.
 */
const SPY_IDS = [
  'modules',
  'collab',
  'pm',
  'ai',
  'knowledge',
  'security',
  'pricing',
  'faq',
] as const;

/**
 * Hai phép gộp của thiết kế (`paintNav`, dòng 1351–1352): nav chỉ có 6 mục cho 8 section theo
 * dõi. Bảng gộp nằm ở đây chứ không trong hook vì nó là quyết định của TRANG, không phải của hạ
 * tầng — `useScrollSpy` không được biết gì về nội dung.
 */
const NAV_ALIASES = { pm: 'collab', knowledge: 'ai' };

/**
 * Drawer nạp thành chunk RIÊNG (design doc §8.7, tầng T3): phần lớn phiên xem trên desktop không
 * bao giờ mở nó. `lazy` chỉ có tác dụng khi component được render có điều kiện — nên đừng
 * "dọn dẹp" bằng cách luôn render rồi ẩn bằng CSS.
 */
const MobileDrawer = lazy(() => import('./MobileDrawer.tsx'));

/**
 * Ngưỡng `xl` của Tailwind, viết bằng `rem` đúng như Tailwind sinh ra — không phải `1280px`.
 *
 * Nó phải khớp `xl:hidden` của hamburger, vì hai thứ này là HAI NỬA của cùng một quyết định: từ
 * `xl` trở lên nav đầy đủ thay thế drawer. Lệch nhau một pixel là có một dải bề rộng mà cả
 * hamburger lẫn nav đều không dùng được.
 */
const XL_QUERY = '(min-width: 80rem)';

export function Header() {
  const copy = useCopy();
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const active = useScrollSpy(SPY_IDS, NAV_ALIASES);
  const homeAnchor = useHomeAnchor();
  const openContact = useContactDialog();

  const closeDrawer = useCallback(() => {
    setOpen(false);
    // Trả focus về chỗ đã mở nó, nếu không thì tiêu điểm rơi về đầu trang và người dùng bàn phím
    // phải Tab lại từ đầu.
    menuButtonRef.current?.focus();
  }, []);

  /**
   * Không mở drawer khi khung nhìn đã ở `xl`.
   *
   * Ở đây chứ không trong effect, vì hai lý do trùng nhau: `react-hooks/set-state-in-effect` cấm
   * gọi setState đồng bộ trong thân effect, và một trạng thái suy được ngay lúc có sự kiện thì
   * đúng ra phải quyết ở sự kiện — không phải mở rồi đóng lại ở lượt render sau.
   *
   * Trong trình duyệt hamburger đã `xl:hidden` nên cú bấm này gần như không xảy ra; phép canh tồn
   * tại để `open` không bao giờ true ở một khổ mà drawer không phải là lối đi đúng.
   */
  const openDrawer = () => {
    if (typeof matchMedia === 'function' && matchMedia(XL_QUERY).matches) return;
    setOpen(true);
  };

  /**
   * Vượt `xl` khi drawer đang mở ⇒ đóng bằng STATE.
   *
   * Bản đầu để drawer tự ẩn bằng `xl:hidden` và đó là một cái bẫy kín: CSS chỉ giấu hộp chứ không
   * unmount, nên effect khoá cuộn của drawer vẫn giữ `body.overflow = hidden`, còn hamburger thì
   * cũng `display:none` ở `xl` ⇒ không còn gì bấm được bằng chuột để đóng. Đo trên bản build (mở
   * ở 1024 rồi resize lên 1400): `dialogDisplay = "none"`, `bodyOverflow = "hidden"`,
   * `scrollTo(0, 500)` cho `scrollY = 0`. Chỉ Escape cứu được. Kịch bản thật không hiếm: cửa sổ
   * ~1200px rồi maximize, hoặc tablet xoay ngang qua 1280.
   *
   * Effect này CHỈ đăng ký lắng nghe. Trạng thái "đã ở `xl` ngay lúc mở" do `openDrawer` chặn từ
   * đầu, nên ở đây không còn ca nào phải sửa ngay lúc chạy.
   *
   * `matchMedia` có thể không tồn tại (jsdom) — cùng lối canh của `useReveal`/`useRotator`.
   */
  useEffect(() => {
    if (!open) return;
    if (typeof matchMedia !== 'function') return;

    const query = matchMedia(XL_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) closeDrawer();
    };
    query.addEventListener('change', onChange);
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, [open, closeDrawer]);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-ink-100 bg-surface/85 backdrop-blur-[14px]">
        {/* Header đặt gutter TRONG bề rộng tối đa, giống hero (`template.html:581`). */}
        <Container gutter="inside" className="flex h-14 items-center gap-7 md:h-[68px]">
          {/*
          `min-h-11` dưới `md`: logo là một LIÊN KẾT, mà nội dung của nó chỉ cao 26px (icon) nên
          vùng chạm không đạt 44px của skill luật 6. Ma trận §11.5 bắt được ở 360 và 390px.
          Chiều cao tăng nhưng hình thức không đổi: header mobile vốn cao 56px, logo canh giữa.
        */}
          <PageLink page="home" className="flex min-h-11 flex-none items-center gap-2.5 md:min-h-0">
            {/* `NewEraMark` tự khai 26×26 và tô bằng `currentColor` ⇒ className ở đây đặt MÀU. */}
            <NewEraMark className="text-brand-600" />
            <span className="font-wordmark text-wordmark font-bold tracking-tight text-ink-900">
              {copy.brand.name}
            </span>
            <span className="hidden border-l border-ink-300 pl-2.5 text-micro text-ink-500 sm:block">
              {copy.brand.byline}
            </span>
          </PageLink>

          <nav
            aria-label={copy.nav.mainNav}
            className="ml-3 hidden items-center gap-[26px] xl:flex"
          >
            {copy.nav.items.map((item) => (
              <a
                key={item.id}
                href={homeAnchor(item.id)}
                aria-current={active === item.id ? 'true' : undefined}
                className={cn(
                  'pb-1 text-body transition [transition-duration:var(--duration-hover)]',
                  active === item.id
                    ? 'font-bold text-brand-600 shadow-[inset_0_-2px_0_0_var(--color-brand-600)]'
                    : 'font-medium text-ink-800 hover:text-brand-600',
                )}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3.5">
            <LanguageSwitcher />

            <a
              href={APP_URL}
              target="_blank"
              // Thiết kế chỉ có `noopener` (dòng 599). `noreferrer` là phần còn thiếu: không thêm
              // thì tab mới nhận được URL trang này trong `Referer`.
              rel="noopener noreferrer"
              className="hidden text-body font-semibold text-ink-900 transition [transition-duration:var(--duration-hover)] hover:text-brand-600 md:block"
            >
              {copy.nav.signIn}
            </a>

            {/*
            Bọc trong `<span>` để ẩn/hiện thay vì đặt `hidden` cạnh class của nút.
            `buttonClasses` đã mang `inline-flex`, mà `cn()` chỉ nối chuỗi chứ không phải
            tailwind-merge ⇒ `hidden` và `inline-flex` cùng tồn tại và thắng thua do THỨ TỰ
            Tailwind xếp utility trong CSS, không do thứ tự ta viết. Bản đầu viết
            `cn(buttonClasses(…), 'hidden sm:inline-flex')` và nút vẫn hiện ở 360px, vỡ thành
            hai dòng trong header — ma trận §11.5 chụp được.
          */}
            <span className="hidden sm:inline-flex">
              <button
                type="button"
                onClick={openContact}
                className={buttonClasses('primary', 'md')}
              >
                {copy.nav.cta}
              </button>
            </span>

            <button
              ref={menuButtonRef}
              type="button"
              aria-expanded={open}
              aria-controls="mobile-drawer"
              aria-label={copy.nav.openMenu}
              onClick={openDrawer}
              className="grid size-11 flex-none place-items-center rounded-pill text-ink-900 transition [transition-duration:var(--duration-hover)] hover:bg-ink-50 xl:hidden"
            >
              {/*
              Ba gạch vẽ tại chỗ, KHÔNG thêm icon `menu` vào registry: registry khoá đúng 41 tên
              `data-lucide` mà thiết kế dùng, và `Icon.test.tsx` so khớp trọn bộ. Thiết kế không
              có bản mobile nên không có hamburger — thêm vào registry là làm hỏng chính bất biến
              "registry = những gì thiết kế dùng".
            */}
              <span aria-hidden className="flex w-5 flex-col gap-[5px]">
                <span className="h-0.5 w-full rounded-pill bg-current" />
                <span className="h-0.5 w-full rounded-pill bg-current" />
                <span className="h-0.5 w-full rounded-pill bg-current" />
              </span>
            </button>
          </div>
        </Container>
      </header>

      {/*
        Drawer render qua PORTAL vào `document.body`, và đó không phải chuyện gu code — nó là bản
        vá cho một lỗi bố cục.

        `header` mang `backdrop-blur`, mà `backdrop-filter` khác `none` TẠO CONTAINING BLOCK cho
        mọi hậu duệ `position: fixed` (filter-effects-2). Drawer là `fixed inset-0`, nên khi nó
        còn nằm trong `header` thì `inset: 0` phân giải theo hộp của header chứ không theo khung
        nhìn. Đo trên bản build ở 390×844: `dialogBox = {top:0, left:0, w:390, h:56}` — một dải
        trắng 56px chỉ chứa nút đóng, còn 6 mục nav + đăng nhập + CTA đổ thẳng lên hero navy,
        chữ `ink-900` trên nền xanh đậm, đè lên h1. Ở 1024 y hệt, dải 68px.

        Nó cũng biến `aria-modal="true"` của drawer thành lời khai sai: một panel không phủ kín
        thì không chặn gì.

        Cách còn lại là hạ `backdrop-blur` xuống một lớp con phủ trong header. Portal an toàn hơn
        về lâu dài: nó cắt hẳn phụ thuộc vào việc header có filter hay không, nên lô sau thêm
        bóng, `filter`, `transform` hay `contain` lên header đều không dựng lại được cái bẫy này.

        Chỉ chạm `document` khi `open` — mà `open` chỉ true sau một cú bấm, tức luôn ở phía trình
        duyệt. Lúc prerender bằng Node (`ViteReactSSG`) nhánh này không bao giờ được tính.
      */}
      {open &&
        createPortal(
          <Suspense fallback={null}>
            <MobileDrawer onClose={closeDrawer} />
          </Suspense>,
          document.body,
        )}
    </>
  );
}
